import {
  feePostRoutes,
  lateFeeOffsetPostRoutes,
} from "features/fees/post/FeePostRoutes";
import {
  getFeeChargeEventBasisBreakdownBySecurityOwnershipRetrieveLoader,
  getFeeChargeEventBasisBreakdownByUseRetrieveLoader,
  getFeeChargeEventListLoader,
  getFeeChargeEventPeriodBreakdownTableDataRetrieveLoader,
  getFeeChargeEventRetrieveLoader,
  getFeeOffsetTableDataForFeeChargeEventRetrieveLoader,
  getFeePeriodTrueUpSummaryTablesRetrieveLoader,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { getFeeOffsettableExpensesTableDataRetrieveLoader } from "gen/endpoints/fee-offsettable-expenses-table-data/fee-offsettable-expenses-table-data";
import { getFeeStatsRetrieveLoader } from "gen/endpoints/fee-stats/fee-stats";
import { getFundFamilyConfigRetrieveLoader } from "gen/endpoints/fund-family-config/fund-family-config";
import i18n from "i18next";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const feeRoutes: RouteObject = {
  id: "fees",
  path: "fees",
  children: [
    {
      id: "feesIndex",
      index: true,
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FeeIndexPage } = await import("features/fees/FeeIndex");
        return { Component: FeeIndexPage };
      },
    },
    {
      id: "feesDashboard",
      path: "dashboard",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FeesPage } = await import(
          "features/fees/dashboard-pages/FeesPage"
        );
        return { Component: FeesPage };
      },
      loader: composeLoaders(getFeeStatsRetrieveLoader()),
      children: [
        {
          id: "feePostingTab",
          path: "postings",
          lazy: async () => {
            const { FeesPostingTab } = await import(
              "features/fees/dashboard-pages/FeePostingTab"
            );
            return { Component: FeesPostingTab };
          },
          handle: {
            title: i18n.t("fees:dashboard.feePostingTab"),
          },
          loader: composeLoaders(
            getFeeStatsRetrieveLoader(),
            getFeeChargeEventListLoader({
              is_equalized_view: false,
            }),
            getFeeOffsettableExpensesTableDataRetrieveLoader(),
          ),
        },
        {
          id: "feeBasisTab",
          path: "basis",
          lazy: async () => {
            const { FeesBasisTab } = await import(
              "features/fees/dashboard-pages/FeeBasisTab/FeeBasisTab"
            );
            return { Component: FeesBasisTab };
          },
          handle: {
            title: i18n.t("fees:dashboard.feeBasisTab"),
          },
          loader: composeLoaders(getFeeStatsRetrieveLoader()),
        },
      ],
    },
    {
      id: "draftAllocations",
      path: "draftAllocations",
      lazy: async () => {
        const { DraftAllocationsPage } = await import(
          "features/fees/DraftAllocationsPage"
        );
        return { Component: DraftAllocationsPage };
      },
    },
    {
      id: "historicalAllocations",
      path: ":fundFamilyEventId/historicalAllocations",
      lazy: async () => {
        const { HistoricalAllocationsPage } = await import(
          "features/fees/HistoricalAllocationsPage"
        );
        return { Component: HistoricalAllocationsPage };
      },
    },
    {
      id: "feeChargeEventPosted",
      path: ":feeChargeEventId/posted",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { PostedFeeChargePage } = await import(
          "features/fees/posted/PostedFeeChargePage"
        );
        return { Component: PostedFeeChargePage };
      },
      loader: composeLoaders(
        getFundFamilyConfigRetrieveLoader(),
        getFeeOffsetTableDataForFeeChargeEventRetrieveLoader({
          is_equalized_view: undefined,
        }),
        getFeePeriodTrueUpSummaryTablesRetrieveLoader(),
        getFeeChargeEventRetrieveLoader(),
      ),
      children: [
        {
          id: "feeChargeEventPostedBreakdown",
          path: "",
          lazy: async () => {
            const { PostedFeeChargeBreakdownTab } = await import(
              "features/fees/posted/PostedFeeChargeBreakdownTab"
            );
            return { Component: PostedFeeChargeBreakdownTab };
          },
          handle: {
            title: i18n.t("fees:postFees.feesBreakdown"),
          },
          loader: composeLoaders(
            getFeeChargeEventPeriodBreakdownTableDataRetrieveLoader({
              is_equalized_view: undefined,
            }),
          ),
        },
        {
          id: "feeChargeEventPostedInvestor",
          path: "by-investor",
          lazy: async () => {
            const { PostedFeeChargeByInvestorTab } = await import(
              "features/fees/posted/PostedFeeChargeByInvestorTab"
            );
            return { Component: PostedFeeChargeByInvestorTab };
          },
          handle: {
            title: i18n.t("fees:postFees.feesBasisBreakdownByInvestorEntity"),
          },
          loader: composeLoaders(
            getFeeChargeEventBasisBreakdownBySecurityOwnershipRetrieveLoader({
              is_equalized_view: undefined,
            }),
          ),
        },
        {
          id: "feeChargeEventPostedUse",
          path: "by-use",
          lazy: async () => {
            const { PostedFeeChargeByUseTab } = await import(
              "features/fees/posted/PostedFeeChargeByUseTab"
            );
            return { Component: PostedFeeChargeByUseTab };
          },
          handle: {
            title: i18n.t("fees:postFees.feesBasisBreakdownByUse"),
          },
          loader: composeLoaders(
            getFeeChargeEventBasisBreakdownByUseRetrieveLoader({
              is_equalized_view: undefined,
            }),
          ),
        },
      ],
    },
    {
      id: "feeBreakdownManagementFeeCatchUp",
      path: ":feeChargeEventId/posted/catch-up",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FeePeriodCatchUpBreakdownPage } = await import(
          "features/fees/true_up_breakdown_pages/FeePeriodCatchUpBreakdownPage"
        );
        return { Component: FeePeriodCatchUpBreakdownPage };
      },
    },
    {
      id: "feeBreakdownCancelCorrectFeeAdjustments",
      path: ":feeChargeEventId/posted/cancel-correct-fee-adjustments",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FeePeriodCancelCorrectFeeAdjustmentsBreakdownPage } =
          await import(
            "features/fees/true_up_breakdown_pages/FeePeriodCancelCorrectFeeAdjustmentsBreakdownPage"
          );
        return { Component: FeePeriodCancelCorrectFeeAdjustmentsBreakdownPage };
      },
    },
    {
      id: "feeBreakdownHistoricalBasisFeeTrueUp",
      path: ":feeChargeEventId/posted/historical-basis-true-up",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FeePeriodHistoricalBasisTrueUpBreakdownPage } = await import(
          "features/fees/true_up_breakdown_pages/FeePeriodHistoricalBasisTrueUpBreakdownPage"
        );
        return { Component: FeePeriodHistoricalBasisTrueUpBreakdownPage };
      },
    },
    feePostRoutes,
    lateFeeOffsetPostRoutes,
  ],
};
